// frontend/src/components/FAQ/FAQ.tsx
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import "./FAQ.css";

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "Is this service really free?",
    answer:
      "Yes, AcceptingMD is completely free for patients. There are no hidden fees, subscriptions, or costs to use the service. AcceptingMD helps Ontario residents connect with participating family doctors who are accepting new patients.",
  },
  {
    question: "How long does it take to hear back from a clinic?",
    answer:
      "Response times vary by clinic. Your request goes directly to the clinic, and AcceptingMD does not control the clinic’s intake decision. You’ll be notified by email when there’s an update. If the clinic does not take action within 7 days, the request will expire and you’ll be notified.",
  },
  {
    question: "Do I need to create an account?",
    answer:
      "No account is required. We’ve designed the process to be as simple as possible. You provide basic contact information so your request can be verified, you can receive status updates, and the clinic can contact you if needed.",
  },
  {
    question: "What information do clinics see about me?",
    answer:
      "Clinics see the information you provide with your request, such as your name, contact details, date of birth, general location, and the request information needed for the clinic to review your application. The standard online request does not ask you to provide medical records or a detailed medical history.",
  },
  {
    question: "What happens after I submit a request?",
    answer:
      "After you submit and verify your request, the clinic reviews it based on its current capacity and practice scope. The clinic may invite you for a Meet & Greet, place your request on a waitlist, or be unable to accept your request. If you are invited, the clinic will contact you with next steps. An invitation for a Meet & Greet does not by itself confirm attachment to the practice.",
  },
  {
    question: "Can I get help using the online request?",
    answer:
      "Yes, Community Navigator assistance is available in participating Ontario Health Team (OHT) regions for people who need help using the online request process. It is currently available through Connected Care Halton OHT for residents of Oakville, Milton, and Halton Hills. Community Navigators can help with the online process and identify participating family doctor options, but clinics continue to make their own intake decisions.",
  },
];

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    className={`ocf-faq-chevron ${isOpen ? "ocf-faq-chevron-open" : ""}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData).replace(/</g, "\\u003c")}
        </script>
      </Helmet>

      <section className="ocf-faq" aria-label="Frequently asked questions">
        <div className="ocf-faq-inner">
          <h2 className="ocf-faq-title">Frequently Asked Questions</h2>

          <div className="ocf-faq-list">
            {faqItems.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={index} className="ocf-faq-item">
                  <button
                    className="ocf-faq-question"
                    onClick={() => toggleItem(index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span>{item.question}</span>
                    <ChevronIcon isOpen={isOpen} />
                  </button>
                  <div
                    id={`faq-answer-${index}`}
                    className={`ocf-faq-answer ${isOpen ? "ocf-faq-answer-open" : ""}`}
                    role="region"
                    aria-hidden={!isOpen}
                  >
                    <div className="ocf-faq-answer-content">{item.answer}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
